/**
 * CGFinder API Manager
 * Handles all API requests, caching, and data transformation
 */
class CGFinderAPI {
  constructor(baseUrl = 'https://dsk889dwqylbo.cloudfront.net/api/v1/cgfinder') {
    this.baseUrl = baseUrl;
   
    this.cache = {
      years: null,
      models: {},
      details: {},
      categories: null
    };
    this.performanceMetrics = {
      apiCalls: 0,
      cacheHits: 0
    };
  }

  /**
   * Load years from API with caching
   */
  async loadYears() {
    if (this.cache.years) {
      this.performanceMetrics.cacheHits++;
      return this.cache.years;
    }

    try {
      const data = await this.apiRequest('/years');
      this.cache.years = data.data;
      return this.cache.years;
    } catch (error) {
      throw new Error(`Failed to load years: ${error.message}`);
    }
  }

  /**
   * Load models for a specific year with caching 2
   */
  async loadModels(yearText) {
    if (this.cache.models[yearText]) {
      this.performanceMetrics.cacheHits++;
      return this.cache.models[yearText];
    }

    try {
      this.performanceMetrics.apiCalls++;
      const data = await this.apiRequest(`/models?year=${encodeURIComponent(yearText)}`);
      this.cache.models[yearText] = data.data;
      return this.cache.models[yearText];
    } catch (error) {
      throw new Error(`Failed to load models for ${yearText}: ${error.message}`);
    }
  }

  /**
   * Load details for specific year and model with caching
   */
  async loadDetails(yearText, modelText) {
    const cacheKey = this.createCacheKey(yearText, modelText);
    
    if (this.cache.details[cacheKey]) {
      this.performanceMetrics.cacheHits++;
      return this.cache.details[cacheKey];
    }

    try {
      this.performanceMetrics.apiCalls++;
      const endpoint = `/details?model=${encodeURIComponent(modelText)}&year=${encodeURIComponent(yearText)}`;
      const data = await this.apiRequest(endpoint);
      this.cache.details[cacheKey] = data.data;
      return this.cache.details[cacheKey];
    } catch (error) {
      throw new Error(`Failed to load details for ${yearText} ${modelText}: ${error.message}`);
    }
  }

  /**
   * Check if SKUs fit with a specific vehicle (by metaobjectId)
   * POST /api/v1/cgfinder/check-fitment
   * @param {string[]} skus - Array of SKU strings to check
   * @param {string} metaobjectId - Vehicle metaobject ID (e.g., "gid://shopify/Metaobject/123")
   * @returns {Promise<{metaobjectId: string, results: Array<{sku: string, fit: boolean}>}>}
   */
  async checkFitment(skus, metaobjectId) {
    if (!skus || !Array.isArray(skus) || skus.length === 0) {
      throw new Error('SKUs array is required and must not be empty');
    }
    
    if (!metaobjectId) {
      throw new Error('metaobjectId is required');
    }

    try {
      this.performanceMetrics.apiCalls++;
      const data = await this.apiRequestPost('/check-fitment', {
        skus,
        metaobjectId
      });
      return data.data;
    } catch (error) {
      throw new Error(`Failed to check fitment: ${error.message}`);
    }
  }

  /**
   * Make standardized GET API request
   */
  async apiRequest(endpoint) {
    const response = await fetch(`${this.baseUrl}${endpoint}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }
    
    return data;
  }


  /**
   * Make standardized POST API request
   */
  async apiRequestPost(endpoint, body) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'API request failed');
    }
    
    return data;
  }

  /**
   * Create cache key for details
   */
  createCacheKey(...parts) {
    return parts.join('-');
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return { ...this.performanceMetrics };
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.cache = {
      years: null,
      models: {},
      details: {},
      categories: null
    };
    this.performanceMetrics = {
      apiCalls: 0,
      cacheHits: 0
    };
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.CGFinderAPI = CGFinderAPI;
}